import { Inertia } from '@inertiajs/inertia'
import { Component, Element, Fragment, FunctionalComponent, h, Host, Listen, Prop, Watch } from '@stencil/core'
import { JSXBase, State, writeTask } from '@stencil/core/internal'
import { ObservableMap } from '@stencil/store'
import Axios from 'axios'
import { IAiNoteSource } from 'src/ai-note/types'
import { IAskFathomProps } from 'src/ask-fathom/types'
import { css } from 'src/lib/emotion'
import { IPlaylistActions } from 'src/playlist/types'
import { PlaylistChip } from 'src/playlist/ui'
import { copyShareUrl, getPlaylistShareDisabledReason } from 'src/playlist/utils'
import { SubscriptionProductPlan } from 'src/subscription/types'
import { storeForCrm } from 'src/utils/crm'

import { SharedProps } from '../../../app'
import {
  ICall,
  ICallComment,
  ICallLiveInterval,
  ICallSharing,
  IContact,
  INote,
  IPageCallDetailFeatures,
  ISpeaker,
} from '../../../call/types'
import { CloseioIntegrationState, CloseioIntegrationStore } from '../../../crm/closeio/state'
import { HubspotIntegrationState, HubspotIntegrationStore } from '../../../crm/hubspot/state'
import { SalesforceIntegrationState, SalesforceIntegrationStore } from '../../../crm/salesforce/state'
import { Menu, MenuItem } from '../../../ui/menu/menu'
import { Deferred, Queue } from '../../../utils/async'
import { classes } from '../../../utils/css'
import { reloadInertiaProp, reloadInertiaPropsBulk } from '../../../utils/inertia'
import { logWithBadge } from '../../../utils/logging'
import { sendLogs } from '../../../utils/new_relic'
import { convertFromPlayerTime, convertToPlayerTime } from '../../../utils/player-time'
import { removeServiceWorker } from '../../../utils/service-worker'
import { IUser } from '../../admin/types'
import { DetailPlayerSubNavTab, isSummaryTab } from './player-sub-nav/types'
import {
  addNote,
  CallStore,
  deleteAutoGenerated,
  deleteNote,
  endTrimming,
  ICallState,
  INotesState,
  IPageCallDetailActions,
  IPlaybackState,
  ITranscriptState,
  nextDisplayMode,
  NotesStore,
  PlaybackStore,
  TranscriptStore,
  updateNote,
} from './state'
import { ICallActionItemAssignment, ICallActionItemDetection, ICallAiNotes, ICallFollowUpEmail } from './types'
import { CueSpan, NoteSpan, ProcessingSpan, SearchMarker } from './ui'

const aspect = css`
  position: relative;
  video {
    position: absolute;
    top: 0;
  }
  &:before {
    content: '';
    display: block;
    width: 100%;
    padding-top: calc(9 / 16 * 100%); // 16:9
    min-height: 250px;
  }
`

const VideoSizeIcon: FunctionalComponent<{ mode: IPlaybackState['displayMode'] } & JSXBase.IntrinsicElements['div']> = (
  { mode, class: className = '', ...attrs },
  children
) => {
  return (
    <div {...attrs} class={`w-4 h-3 text-sm border rounded-sm p-1px border-glyph-light ${className}`}>
      <div
        class={{
          'h-1/2 w-1/2 bg-white': mode === 'normal',
          'h-full w-1/2 bg-white': mode === 'theater',
          'h-full w-full bg-white': mode === 'fullscreen',
        }}
      />
      {children}
    </div>
  )
}

@Component({
  tag: 'page-call-detail',
})
export class CallPage {
  @Prop() callsUrl: string
  @Element() el: HTMLPageCallDetailElement
  @Prop() access: ICallState['access']
  @Prop({ mutable: true }) attendeeMetadata: ICallState['attendeeMetadata']
  @Prop({ mutable: true }) relatedCall: ICallState['relatedCall']
  @Prop({ mutable: true }) call: ICallState['call']
  @Prop({ mutable: true }) notes: ICallState['notes'] = []
  @Prop({ mutable: true }) bookmarks: ICallState['bookmarks'] = []
  @Prop({ mutable: true }) highlightReels: ICallState['highlightReels'] = []
  @Prop({ mutable: true }) chatMessages: ICallState['chatMessages'] = []
  @Prop({ mutable: true }) questions: ICallState['questions'] = { clips: [], duration_seconds: null, speakers: [] }
  @Prop({ mutable: true }) duration: IPlaybackState['duration']
  @Prop({ mutable: true }) noteClips: {
    clips: INotesState['noteClips']
    total_clip_duration: INotesState['totalClipDuration']
    bookmarks: INotesState['bookmarks']
  } = { clips: [], total_clip_duration: null, bookmarks: [] }
  @Prop({ mutable: true }) transcriptCues: ITranscriptState['transcript'] = []
  @Prop({ mutable: true }) cueSpans: IPlaybackState['cueSpans'] = []
  @Prop({ mutable: true }) liveIntervals: ICallLiveInterval[] = []
  @Prop({ mutable: true }) updatedLiveIntervals: ICallLiveInterval[] = []
  @Prop({ mutable: true }) playlistMetadata: ICallState['playlistMetadata']
  @Prop({ mutable: true }) followUpEmail: ICallFollowUpEmail
  @Prop({ mutable: true }) actionItemDetection: ICallActionItemDetection
  @Prop({ mutable: true }) actionItemAssignment: ICallActionItemAssignment
  @Prop({ mutable: true }) askFathom: IAskFathomProps
  @Prop({ mutable: true }) aiNotes: ICallAiNotes
  @Prop({ mutable: true }) comments: ICallComment[] = []
  @Prop({ mutable: true }) sharing: ICallSharing

  @Prop() pusherConfig: Array<{ channel: string; event: string }>
  @Prop() pusherClient: typeof Pusher
  @Prop() newRelicLogsUrl: string
  @Prop() currentUser: IUser
  @Prop() env: SharedProps['env']
  @Prop() recordingsUrl: string
  @Prop() customizeUrl: string
  @Prop() noteUrl = ''
  @Prop() actionItemUrl = ''
  @Prop() autoGeneratedActionItemsUrl = ''
  @Prop({ mutable: true }) allContacts: IContact[] = []
  @Prop({ mutable: true }) integrations: {
    salesforce: Partial<SalesforceIntegrationState>
    hubspot: Partial<HubspotIntegrationState>
    closeio: Partial<CloseioIntegrationState>
  } = {
    salesforce: {},
    hubspot: {},
    closeio: {},
  }

  @Prop() activateDownloadUrl: string
  @Prop() downloadVideoUrl: string
  @Prop() finishedGuidedWalkthroughUrl = ''
  @Prop() guidedWalkthroughWistiaId: string
  @Prop() currentTime = 0
  @Prop() initialTab: DetailPlayerSubNavTab
  @Prop() clipboardActionItemsUrl: string
  @Prop() copyTranscriptUrl: string
  @Prop() replaceCuesUrl: string
  @Prop() trimRecordingUrl: string
  @Prop() showCrmSync = false
  @Prop() showMisattributedTranscriptCuesBanner = false
  @Prop() searchPlaylistsUrl: string
  @Prop({ mutable: true }) speakers: ISpeaker[] = []
  @Prop({ mutable: true }) bootstrap: Queue<{
    callReady: Deferred
    transcriptCues: Deferred
    durationSet: Deferred
    propsLoaded: Deferred
  }>
  @Prop() actions: IPageCallDetailActions
  @Prop() features: IPageCallDetailFeatures
  @Prop() playlistActions: Pick<IPlaylistActions, 'createUrl' | 'indexUrl'>
  @Prop() showAiModifierBanner = false

  @State() recordingSent = false
  @State() activeTab: DetailPlayerSubNavTab = 'transcript'
  @State() isUserPremiumRequired = false
  @State() showCommentsAnimation = false
  @State() deletedAutoGeneratedNotes = false

  notesEl: HTMLPageCallDetailNotesElement
  videoPlayerEl: HTMLVideoPlayerElement

  initializing = true
  pusherConnected: any
  reloadingNotes: any

  @Watch('call')
  lookForCallChanges(newValue: ICall, oldValue: ICall) {
    if (newValue.id !== oldValue.id) {
      this.attendeeMetadata = {}
      this.playlistMetadata = null
      this.relatedCall = null
      this.componentDidLoad()
    }
  }

  @Watch('notes')
  @Watch('chatMessages')
  @Watch('attendeeMetadata')
  @Watch('relatedCall')
  @Watch('questions')
  @Watch('call')
  @Watch('bookmarks')
  @Watch('highlightReels')
  @Watch('access')
  @Watch('duration')
  @Watch('noteClips')
  @Watch('transcriptCues')
  @Watch('cueSpans')
  @Watch('speakers')
  @Watch('downloadVideoUrl')
  @Watch('showMisattributedTranscriptCuesBanner')
  @Watch('playlistMetadata')
  @Watch('actions')
  updateStores() {
    CallStore.state.access = this.access
    CallStore.state.actionItemUrl = this.actionItemUrl
    CallStore.state.actions = this.actions || {}
    CallStore.state.bookmarks = this.bookmarks
    CallStore.state.call = this.call
    CallStore.state.chatMessages = this.chatMessages
    CallStore.state.highlightReels = this.highlightReels
    CallStore.state.notes = this.notes
    CallStore.state.questions = this.questions
    CallStore.state.showCrmSync = this.showCrmSync
    CallStore.state.relatedCall = this.relatedCall
    CallStore.state.attendeeMetadata = this.attendeeMetadata
    CallStore.state.speakers = this.speakers
    CallStore.state.downloadVideoUrl = this.downloadVideoUrl
    CallStore.state.activateDownloadUrl = this.activateDownloadUrl
    CallStore.state.showMisattributedTranscriptCuesBanner = this.showMisattributedTranscriptCuesBanner
    CallStore.state.searchPlaylistsUrl = this.searchPlaylistsUrl
    CallStore.state.playlistMetadata = this.playlistMetadata
    HubspotIntegrationStore.state.taskSyncEnabled = this.call?.hubspot_task_sync_enabled
    NotesStore.state.actionItemsCompleted = this.notes.filter((note) => note.acknowledged_at).map((note) => note.id)
    NotesStore.state.bookmarks = this.noteClips?.bookmarks || []
    NotesStore.state.isSaving = false
    NotesStore.state.noteClips = this.noteClips?.clips || []
    NotesStore.state.noteUrl = this.noteUrl
    NotesStore.state.playlistActions = this.playlistActions
    NotesStore.state.totalClipDuration = this.noteClips?.total_clip_duration || null
    PlaybackStore.state.cueSpans = this.cueSpans
    PlaybackStore.state.duration = this.duration
    TranscriptStore.state.copyTranscriptUrl = this.copyTranscriptUrl
    TranscriptStore.state.replaceCuesUrl = this.replaceCuesUrl
    TranscriptStore.state.transcript = this.transcriptCues
    TranscriptStore.state.trimRecordingUrl = this.trimRecordingUrl
  }

  @Watch('actionItemDetection')
  onActionItemDetection() {
    CallStore.state.actionItemDetection = this.actionItemDetection
  }

  @Watch('aiNotes')
  onAiNotes() {
    CallStore.state.aiNotes = this.aiNotes
    CallStore.state.showSummaryTab =
      !!CallStore.state.aiNotes?.actions?.createUrl || CallStore.state.aiNotes?.notes?.length > 0
    if (!CallStore.state.showSummaryTab && this.activeTab === 'summary') {
      this.activeTab = 'transcript'
    }
  }

  @Watch('askFathom')
  onAskFathom() {
    CallStore.state.showAskFathomTab = !!this.askFathom?.actions?.messagesUrl
  }

  @Watch('followUpEmail')
  onFollowUpEmail() {
    CallStore.state.followUpEmail = this.followUpEmail
  }

  @Watch('actionItemAssignment')
  onActionItemAssignment() {
    CallStore.state.actionItemAssignment = this.actionItemAssignment
  }

  @Watch('integrations')
  updateSalesforceStore({ salesforce = {}, hubspot = {}, closeio = {} }: this['integrations']) {
    for (const [key, value] of Object.entries(salesforce)) {
      SalesforceIntegrationStore.state[key] = value
    }
    for (const [key, value] of Object.entries(hubspot)) {
      HubspotIntegrationStore.state[key] = value
    }
    for (const [key, value] of Object.entries(closeio)) {
      CloseioIntegrationStore.state[key] = value
    }
  }

  @Watch('pusherClient')
  @Watch('pusherConfig')
  initPusher() {
    if (this.pusherClient && this.pusherConfig && !this.pusherConnected) {
      const channelClientMap = {}
      this.pusherConnected = true
      this.pusherConfig.map(({ channel, event }) => {
        let c = channelClientMap[channel]
        if (!c) {
          c = this.pusherClient.subscribe(channel)
          channelClientMap[channel] = c
        }
        c.bind(event, (data) => {
          if (this.initializing) {
            return
          }
          sendLogs(this.newRelicLogsUrl, {
            pusher: {
              action: 'receive',
              channel: channel,
              event: event,
              data: data,
            },
            frontend: {
              version: this.env.APP_VERSION,
              component: 'page-call-detail',
            },
          })

          logWithBadge('Pusher', channel, event, data)

          if (event === 'mp4-created') {
            Inertia.reload({ only: ['downloadVideoUrl'] })
          } else if (event === 'crm-sync-complete') {
            storeForCrm(data.crm).state.callCurrentlySyncing = false
            Inertia.reload({ only: ['integrations'] })
          } else if (event === 'crm-sync-in-progress') {
            storeForCrm(data.crm).state.callCurrentlySyncing = true
          } else if (event === 'crm-external-contact-sync-in-progress') {
            storeForCrm(data.crm).state.contactsCurrentlySyncing = true
          } else if (event === 'crm-external-contact-sync-complete') {
            storeForCrm(data.crm).state.contactsCurrentlySyncing = false
            storeForCrm(data.crm).state.refreshingContacts = true
            Inertia.reload({ only: ['integrations', 'attendeeMetadata'] })
          } else if (event.startsWith('crm') || event.startsWith('salesforce')) {
            Inertia.reload({ only: ['integrations'] })
          } else if (event.startsWith('transcript')) {
            this.reload(true).then(endTrimming)
          } else if (event.startsWith('preferences')) {
            Inertia.reload({ only: ['call'] })
          } else if (event.startsWith('notes')) {
            this.onNotesUpdated()
          } else if (event.startsWith('attached-hls-playlist')) {
            this.videoPlayerEl.reload(PlaybackStore.state.currentTime)
            Inertia.reload({ only: ['liveIntervals', 'updatedLiveIntervals'] })
          } else if (event.startsWith('team-view-visibility')) {
            Inertia.reload({ only: ['call'] })
          } else if (event === 'note-mp4-created') {
            Inertia.reload({ only: ['noteClips'] })
          } else if (event === 'note-share-url-created') {
            Inertia.reload({ only: ['noteClips'] })
          } else if (event === 'playlist-updated') {
            Inertia.reload({ only: ['playlistMetadata'] })
          } else if (event === 'action-item-detection-updated') {
            Inertia.reload({ only: ['actionItemDetection'] })
          } else if (event === 'action-item-assignments-updated') {
            Inertia.reload({ only: ['noteClips'] })
          } else if (event === 'follow-up-email-updated') {
            Inertia.reload({ only: ['followUpEmail'] })
          } else if (event === 'call-comments-updated') {
            Inertia.reload({ only: ['comments'] })
          } else if (event === 'templated-note-updated') {
            this.reloadAiNote(data.templated_note_id)
          } else if (event === 'call-share-token-audiences-updated') {
            Inertia.reload({ only: ['sharing'] })
          } else {
            // reload m3u8 playlist when the call is ready or finalized
            if (event.startsWith('state')) {
              this.videoPlayerEl.reload(PlaybackStore.state.currentTime)
              Inertia.reload({ only: ['liveIntervals', 'updatedLiveIntervals'] })
            }
            this.reload(false)
          }
        })
      })
    }
  }

  reload(cuesToo: boolean) {
    const props = ['call', 'notes', 'duration', 'askFathom']
    if (cuesToo) {
      props.push('noteClips', 'questions', 'transcriptCues', 'cueSpans', 'updatedLiveIntervals')
    }
    return reloadInertiaPropsBulk('page-call-detail', props).then((data) => {
      for (const [prop, propVal] of Object.entries(data)) {
        this[prop] = propVal
      }
    })
  }

  async reloadAiNote(aiNoteId: number) {
    // Find the aiNote to reload
    const allNotes = CallStore.state.aiNotes?.notes?.flatMap((note) => [note, ...note.translations]) || []
    const aiNote = allNotes.find((storedAiNote) => storedAiNote.id === aiNoteId)

    // If the aiNote is not found (e.g., because another user is creating the aiNote), then reload everything
    if (!aiNote) {
      await reloadInertiaProp('page-call-detail', 'aiNotes').then((response) => {
        this.aiNotes = response.data.props.aiNotes
      })
      return
    }

    let existingAiNoteSource
    if ('sourceTemplatedNoteId' in aiNote) {
      // Get the source aiNote from the updated translation that came from pusher:
      existingAiNoteSource = CallStore.state.aiNotes.notes.find(
        (storedAiNote) => storedAiNote.id === aiNote.sourceTemplatedNoteId
      )
    } else {
      // Updated templated note from pusher was the source templated note:
      existingAiNoteSource = aiNote
    }

    // If the aiNote is found, then try to reload it
    try {
      const { data } = await Axios.get<IAiNoteSource>(existingAiNoteSource.actions.showUrl)
      CallStore.state.aiNotes = {
        ...CallStore.state.aiNotes,
        notes: CallStore.state.aiNotes.notes.map((aiNoteSource) => {
          if (aiNoteSource.id === existingAiNoteSource.id) {
            // Update with the new data that came back from the API:
            return data
          }
          return aiNoteSource
        }),
      }
      // TODO: Once aiNotes are surfaced on the call share page, we'll
      //        need to fetch the corresponding template as well (because
      //        only the templates for the already-generated notes will
      //        be passed to the frontend)
    } catch (ex) {
      // TODO: retry?
    }
  }

  async componentWillLoad() {
    this.initPusher()

    removeServiceWorker()

    this.bootstrap = new Queue({
      callReady: new Deferred(),
      transcriptCues: new Deferred(),
      durationSet: new Deferred(),
      propsLoaded: new Deferred(),
    })

    this.onChangeListener(CallStore, 'call', (call) => {
      if (['ready', 'finalized'].includes(call.state)) {
        this.bootstrap.resolve('callReady')
        logWithBadge('Bootstrap', 'callReady')
      }
    })

    this.onChangeListener(CallStore, 'requiredPlan', (requiredPlan: SubscriptionProductPlan) => {
      // TODO(nkraft): extend this to TE plans
      this.isUserPremiumRequired = requiredPlan === 'user_premium'
    })

    this.onChangeListener(PlaybackStore, 'duration', (duration) => {
      if (duration > 0) {
        this.bootstrap.resolve('durationSet')
        logWithBadge('Bootstrap', 'durationSet')
      }
    })

    this.onChangeListener(TranscriptStore, 'transcript', (transcript) => {
      if (transcript.length > 0) {
        this.bootstrap.resolve('transcriptCues')
        logWithBadge('Bootstrap', 'transcriptCues')
      }
    })

    this.recordingSent = this.call.sharedRecordingWithAttendees

    document.querySelector('inertia-app')?.waitFor(this.bootstrap.promise)

    this.updateSalesforceStore(this.integrations)
    this.updateStores()
  }

  storeListeners: Array<() => void> = []

  onChangeListener<T, K extends keyof T>(store: ObservableMap<T>, prop: K, callback: (newValue: T[K]) => void) {
    this.storeListeners.push(store.onChange(prop, callback))
  }

  async onNotesUpdated() {
    if (this.reloadingNotes) {
      logWithBadge('Reload Notes', 'Already loading, abort')
      return
    }
    this.reloadingNotes = true

    const toUpdate = ['notes', 'highlightReels', 'noteClips', 'cueSpans', 'transcriptCues']

    logWithBadge('Reload Notes', 'Start reload', toUpdate)

    await reloadInertiaPropsBulk('page-call-detail', toUpdate).then((data) => {
      for (const [prop, propVal] of Object.entries(data)) {
        this[prop] = propVal
      }
    })
    this.reloadingNotes = false
    logWithBadge('Reload Notes', 'Done')
  }

  componentDidLoad() {
    // Necessary for initial render
    reloadInertiaPropsBulk('page-call-detail', [
      'call',
      'bookmarks',
      'cueSpans',
      'noteClips',
      'notes',
      'transcriptCues',
    ]).then((data) => {
      for (const [prop, propVal] of Object.entries(data)) {
        this[prop] = propVal
      }

      this.onChangeListener(PlaybackStore, 'duration', () => {
        Inertia.reload({ only: ['noteClips', 'transcriptCues', 'cueSpans'] })
      })

      this.onChangeListener(PlaybackStore, 'seekTime', async (time) => {
        if (time !== null) {
          await this.videoPlayerEl.play(time)
          PlaybackStore.state.seekTrigger = null
          PlaybackStore.state.seekId = null
        }
      })

      this.onChangeListener(PlaybackStore, 'currentTime', async (time) => {
        if (time !== null) {
          const firstAfterIndex = this.updatedLiveIntervals.findIndex((interval) => interval.start_time > time)
          if (firstAfterIndex > 0) {
            const current = this.updatedLiveIntervals[firstAfterIndex - 1]
            const next = this.updatedLiveIntervals[firstAfterIndex]
            if (current && next && time > current.end_time && time !== next.start_time) {
              // don't skip if we're already set... probably some recursion here
              this.videoPlayerEl.setCurrentTime(next.start_time)
            }
          }
        }
      })

      this.onChangeListener(TranscriptStore, 'foundCue', (cue) => {
        if (cue) {
          this.videoPlayerEl.setCurrentTime(cue.start_time)
        }
      })

      this.onChangeListener(PlaybackStore, 'paused', (value) => {
        value ? this.videoPlayerEl.pause() : this.videoPlayerEl.play()
      })

      this.onChangeListener(PlaybackStore, 'displayMode', (value) => {
        if (value === 'fullscreen') {
          this.el.requestFullscreen()
        } else if (document.fullscreenElement) {
          document.exitFullscreen()
        }
      })

      reloadInertiaPropsBulk('page-call-detail', [
        'allContacts',
        'integrations',
        'highlightReels',
        'chatMessages',
        'questions',
        'attendeeMetadata',
        'relatedCall',
        'speakers',
        'playlistMetadata',
        'actionItemDetection',
        'followUpEmail',
        'actionItemAssignment',
        'aiNotes',
        'comments',
        'sharing',
        'askFathom',
      ]).then((data) => {
        for (const [prop, propVal] of Object.entries(data)) {
          this[prop] = propVal
        }

        // Delays showing the animation until we're sure there's no comments
        this.showCommentsAnimation = true
      })

      if (this.initialTab) {
        this.activeTab = this.initialTab
      }

      this.initializing = false

      writeTask(() => this.bootstrap.resolve('propsLoaded'))
    })
  }

  @Listen('fullscreenchange', { target: 'document' })
  videoPlayerFullScreenChanged() {
    if (!document.fullscreenElement && PlaybackStore.state.displayMode === 'fullscreen') {
      PlaybackStore.state.displayMode = 'normal'
    }
  }

  @Listen('uiContentElementAdded')
  uiContentElementAdded(event: CustomEvent<HTMLElement>) {
    event.detail.addEventListener('click', () => {
      const timestamp = parseFloat(event.detail.dataset.timestamp)
      if (Number.isFinite(timestamp)) {
        this.videoPlayerEl.play(timestamp)
      }
    })
  }

  disconnectedCallback() {
    this.storeListeners.map((fn) => fn())
    this.storeListeners.length = 0
    CallStore.reset()
    NotesStore.reset()
    PlaybackStore.reset()
    TranscriptStore.reset()
  }

  convertFromPlayerTime(time: number) {
    return convertFromPlayerTime(this.liveIntervals, time)
  }

  convertToPlayerTime(time: number) {
    return convertToPlayerTime(this.liveIntervals, time)
  }

  async deleteNote(note: INote) {
    await deleteNote(note, this.noteUrl)
  }

  async deleteAutoGeneratedNotes() {
    await deleteAutoGenerated(this.autoGeneratedActionItemsUrl)
    this.deletedAutoGeneratedNotes = true
  }

  async updateNote(note: INote) {
    await updateNote(note, this.noteUrl)
  }

  async addNote(note: INote) {
    await addNote(note, this.noteUrl)
  }

  render() {
    const playlistShareDisabledReason = getPlaylistShareDisabledReason(CallStore.state.playlistMetadata?.playlist)

    return (
      <Host class='overflow-hidden mx-auto w-full h-full' style={{ background: 'inherit' }}>
        <div class='sr-only'>
          {/* Preload these components since they don't render initially (due to lazy props) */}
          <call-detail-notes-note-clip key={`call-detail-notes-note-clip-preload`} />
          <page-call-detail-transcript-quote />
        </div>
        <div
          class='flex relative flex-col items-start mx-auto w-full h-full'
          style={{
            maxWidth: PlaybackStore.state.displayMode !== 'normal' ? '100vw' : '917px',
          }}
        >
          {CallStore.state.playlistMetadata?.playlist && (
            <div
              class={`flex justify-between items-center pb-15px w-full border-b border-gray-700 ${classes(
                CallStore.state.isAttendeeView && PlaybackStore.state.displayMode === 'normal',
                'mb-4'
              )} ${classes(!CallStore.state.isAttendeeView, 'pt-15px')} ${classes(
                PlaybackStore.state.displayMode !== 'normal',
                'px-4'
              )}`}
            >
              <PlaylistChip playlist={CallStore.state.playlistMetadata?.playlist} size='lg' />
              {!CallStore.state.isAttendeeView && (
                <div class='flex flex-col'>
                  <div class='flex spacing-x-10px'>
                    <button
                      disabled={!!playlistShareDisabledReason}
                      type='button'
                      class={`
                        flex flex-1 justify-between items-center py-4px px-2
                        text-base font-bold text-brand-base bg-brand-base
                        bg-opacity-10 rounded-md whitespace-no-wrap
                        spacing-x-10px
                        ${classes(
                          playlistShareDisabledReason,
                          'opacity-25 cursor-default',
                          'hover:text-surface-base hover:bg-brand-base cursor-pointer'
                        )}
                      `}
                      onClick={() => {
                        copyShareUrl(CallStore.state.playlistMetadata.playlist)
                      }}
                    >
                      <span>Share Playlist</span>
                      <ui-icon icon='link-45deg' class='text-xl opacity-50' />
                      {playlistShareDisabledReason && (
                        <ui-tooltip anchor='bottom'>{playlistShareDisabledReason}</ui-tooltip>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
          <div
            class='flex relative mx-auto w-full'
            // 80px is the height of the PlaylistChip row
            style={{ height: CallStore.state.playlistMetadata?.playlist ? 'calc(100% - 80px)' : '100%' }}
          >
            <section
              class={`flex flex-col flex-grow h-full mx-auto w-full`}
              style={{ maxWidth: PlaybackStore.state.displayMode !== 'normal' ? null : '530px' }}
            >
              <video-player
                src={this.call.video_url}
                class={`
                relative top-0 left-0 w-full ${aspect}
                ${classes(!PlaybackStore.state.duration, 'invisible')}
                ${classes(PlaybackStore.state.displayMode === 'normal', 'rounded-t-lg sm:mt-4')}
              `}
                ref={(el) => {
                  if (el) {
                    this.videoPlayerEl = el
                  }
                }}
                trackingSessionId={'call-detail-' + CallStore.state.access + '-' + this.call.id}
                muxVideoTitle={CallStore.state.access + ' - ' + this.call.id}
                muxVideoId={`${this.call.id}`}
                muxPageType='call-detail'
                currentTime={this.currentTime}
                convertFromPlayerTime={(time: number) => this.convertFromPlayerTime(time)}
                convertToPlayerTime={(time: number) => this.convertToPlayerTime(time)}
                endTime={PlaybackStore.state.duration}
                onPaused={() => {
                  PlaybackStore.state.paused = true
                }}
                onPlayed={() => {
                  PlaybackStore.state.paused = false
                }}
                onRateChanged={(e) => {
                  PlaybackStore.state.speed = e.detail.playbackRate
                }}
                onTimeUpdated={(e) => {
                  PlaybackStore.state.currentTime = this.convertFromPlayerTime(e.detail.currentTime)
                }}
                onVideoSeeked={(e) => {
                  PlaybackStore.state.currentTime = this.convertFromPlayerTime(e.detail.currentTime)
                }}
                onVideoEnded={() => {
                  if (CallStore.state.playlistMetadata?.nextPlaylistItem?.actions?.showUrl) {
                    Inertia.visit(CallStore.state.playlistMetadata?.nextPlaylistItem.actions.showUrl)
                  }
                }}
                showGradients={false}
                alwaysHideControlsOnMouseLeave
                renderOverlay={() => (
                  // Mobile-only view
                  <div class='lg:hidden absolute top-0 right-0 left-0 m-3'>
                    <video-call-chip
                      onTitleFocused={() => {
                        this.videoPlayerEl.pause()
                      }}
                      onTitleEdited={({ detail }) => {
                        Axios.put(CallStore.state.call.updateUrl, { topic: detail })
                      }}
                      call={CallStore.state.call}
                      currentUser={CallStore.state.readonly ? null : this.currentUser}
                      avatar
                      rating={!CallStore.state.isAttendeeView}
                      date
                      companyName={false}
                    />
                  </div>
                )}
                renderMarkers={() => (
                  <Fragment>
                    {TranscriptStore.state.searchCues.map((cue) => (
                      <SearchMarker
                        cue={cue}
                        found={cue === TranscriptStore.state.foundCue}
                        left={this.convertToPlayerTime(cue.start_time) / PlaybackStore.state.duration}
                        onClick={async () => {
                          await this.videoPlayerEl.play(cue.start_time)
                        }}
                      />
                    ))}
                    {!TranscriptStore.state.autoScroll && (
                      <div
                        class='absolute top-0 w-3px bg-white opacity-75 transition-all duration-200'
                        style={{ height: '22px', left: TranscriptStore.state.scrollPct * 100 + '%' }}
                      />
                    )}
                  </Fragment>
                )}
                renderSpans={() => (
                  <Fragment>
                    {PlaybackStore.state.cueSpans.map((cueSpan) => (
                      <CueSpan
                        cueSpan={cueSpan}
                        left={this.convertToPlayerTime(cueSpan.start_time) / PlaybackStore.state.duration}
                        width={
                          (this.convertToPlayerTime(cueSpan.start_time + cueSpan.duration_seconds) -
                            this.convertToPlayerTime(cueSpan.start_time)) /
                          PlaybackStore.state.duration
                        }
                      />
                    ))}
                    {NotesStore.state.noteClips.map(
                      (noteClip) =>
                        noteClip.note.bookmark.highlight &&
                        noteClip.end_time <= PlaybackStore.state.duration && (
                          <NoteSpan
                            onClick={() => {
                              PlaybackStore.state.seekTrigger = 'player-note'
                              PlaybackStore.state.seekId = noteClip.note.id
                              PlaybackStore.state.seekTime = noteClip.start_time
                            }}
                            onMouseOver={() => {
                              NotesStore.state.highlightId = noteClip.note.id
                            }}
                            onMouseOut={() => {
                              NotesStore.state.highlightId = null
                            }}
                            noteClip={noteClip}
                            left={this.convertToPlayerTime(noteClip.start_time) / PlaybackStore.state.duration}
                            width={
                              (this.convertToPlayerTime(noteClip.end_time) -
                                this.convertToPlayerTime(noteClip.start_time)) /
                              PlaybackStore.state.duration
                            }
                            isPending={this.convertToPlayerTime(noteClip.end_time) > PlaybackStore.state.duration}
                          />
                        )
                    )}
                    {
                      <ProcessingSpan
                        pct={
                          CallStore.state.call.processing_duration /
                          (CallStore.state.call.processing_duration + PlaybackStore.state.duration)
                        }
                      />
                    }
                  </Fragment>
                )}
                renderActions={() => (
                  <VideoSizeIcon
                    class='hidden sm:block cursor-pointer'
                    mode={PlaybackStore.state.displayMode}
                    onClick={nextDisplayMode}
                  >
                    <Menu anchor='top' allowClick={false} inline>
                      <MenuItem
                        disabled={PlaybackStore.state.displayMode === 'normal'}
                        onClick={() => (PlaybackStore.state.displayMode = 'normal')}
                        icon={() => <VideoSizeIcon mode='normal' />}
                      >
                        Regular
                      </MenuItem>
                      <MenuItem
                        disabled={PlaybackStore.state.displayMode === 'theater'}
                        icon={() => <VideoSizeIcon mode='theater' />}
                        onClick={() => (PlaybackStore.state.displayMode = 'theater')}
                      >
                        Expanded
                      </MenuItem>
                      <MenuItem
                        disabled={PlaybackStore.state.displayMode === 'fullscreen'}
                        icon={() => <VideoSizeIcon mode='fullscreen' />}
                        onClick={() => (PlaybackStore.state.displayMode = 'fullscreen')}
                      >
                        Full-screen
                      </MenuItem>
                    </Menu>
                  </VideoSizeIcon>
                )}
              />

              {CallStore.state.showSummaryTab && (
                <page-call-detail-player-sub-nav
                  copyTranscriptUrl={this.copyTranscriptUrl}
                  activeTab={this.activeTab}
                  onActiveTabChanged={({ detail }) => (this.activeTab = detail)}
                />
              )}

              <page-call-detail-transcript
                hidden={this.activeTab !== 'transcript'}
                class='flex-grow'
                style={{ marginLeft: PlaybackStore.state.displayMode !== 'normal' ? '-130px' : '-150px' }}
                copyTranscriptUrl={CallStore.state.showSummaryTab ? null : this.copyTranscriptUrl}
                onNoteDeleted={({ detail }) => {
                  this.deleteNote(detail)
                }}
                onNoteEdited={({ detail }) => {
                  this.updateNote(detail)
                }}
                onNoteAdded={({ detail }) => {
                  this.addNote(detail)
                }}
                onUserScrolled={({ detail }) => {
                  if (PlaybackStore.state.paused) {
                    this.videoPlayerEl.setCurrentTimePercent(detail)
                  }
                }}
              />

              <page-call-detail-ai-notes
                hidden={!isSummaryTab(this.activeTab)}
                class='flex-grow bg-black'
                hasTemplateModifiers={this.features?.ai_template_modifiers}
                showAiModifierBanner={this.showAiModifierBanner}
                defaultTemplatedNoteLanguage={this.aiNotes?.defaultLanguage}
                canCreateTranslations={!!this.features?.ai_templated_note_translations}
              />

              <ask-fathom-chat
                class='bg-black'
                hidden={this.activeTab !== 'ask_fathom'}
                askFathom={this.askFathom}
                welcomeMessage='Hi, what can I tell you about this meeting?'
              />
            </section>
            {/* Call view "sidebar" */}
            <page-call-detail-notes
              ref={(el) => (this.notesEl = el)}
              class={`
                h-full flex-shrink-0 hidden
                ${classes(PlaybackStore.state.displayMode !== 'normal', 'md:flex', 'flex-1 lg:flex')}
              `}
              style={{ maxWidth: '385px' }}
              allContacts={this.allContacts}
              activateDownloadUrl={this.activateDownloadUrl}
              clipboardActionItemsUrl={this.clipboardActionItemsUrl}
              onDelete={(e) => this.deleteNote(e.detail)}
              onDeleteAllAutoGenerated={() => this.deleteAutoGeneratedNotes()}
              onEdit={(e) => this.updateNote(e.detail)}
              onSharedRecordingWithAttendees={() => (this.recordingSent = true)}
              recordingsUrl={this.recordingsUrl}
              customizeUrl={this.customizeUrl}
              comments={this.comments}
              sharing={this.sharing}
              showCommentsAnimation={this.showCommentsAnimation}
              deletedAutoGenerated={this.deletedAutoGeneratedNotes}
            />
          </div>
        </div>
        {this.finishedGuidedWalkthroughUrl && (
          <ui-guided-walkthrough
            onCompleted={() => {
              Axios.post(this.finishedGuidedWalkthroughUrl)
            }}
            guidedWalkthroughWistiaId={this.guidedWalkthroughWistiaId}
          />
        )}
        {this.isUserPremiumRequired && (
          <user-premium-required
            upgradeUrl={this.actions.premiumPlanUpgradeUrl}
            onCancel={() => {
              CallStore.state.requiredPlan = null
            }}
          />
        )}
      </Host>
    )
  }
}
